import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, CheckCircle2, ChevronLeft, ChevronRight, Flag, GraduationCap, Heart, Lock, MessagesSquare, MoreHorizontal, Plane, Send, Shield, Users } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MISSION_INFO } from "@/lib/constants";
import { useSaveToggle } from "@/hooks/use-save-toggle";
import { useToast } from "@/components/toast";
import { ConfirmRequestDialog } from "@/components/confirm-request";
import { MemberActionsDialog } from "@/components/member-actions";
import { Button, ErrorState, Loading, Pill } from "@/components/ui";

function Gallery({ photos, page, setPage, name }) {
  const go = (d) => setPage((page + d + photos.length) % photos.length);
  const nav = "absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow transition-transform hover:scale-105";
  return (
    <>
      {photos.map((p, i) => (
        <img key={p} src={photoUri(p)} alt={`${name} photo ${i + 1}`} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === page ? "opacity-100" : "opacity-0"}`} />
      ))}
      {photos.length > 1 ? (
        <>
          <button data-testid="member-photo-prev" aria-label="Previous photo" onClick={() => go(-1)} className={`${nav} left-3`}><ChevronLeft className="h-5 w-5" /></button>
          <button data-testid="member-photo-next" aria-label="Next photo" onClick={() => go(1)} className={`${nav} right-3`}><ChevronRight className="h-5 w-5" /></button>
          <div className="absolute inset-x-0 top-3 flex justify-center gap-1.5">
            {photos.map((p, i) => <span key={p} className={`h-1.5 rounded-full transition-all ${i === page ? "w-6 bg-white" : "w-1.5 bg-white/60"}`} />)}
          </div>
        </>
      ) : null}
    </>
  );
}

export default function MemberDetail() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { id } = useParams();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(0);
  const [actions, setActions] = useState(false);
  const toggleSave = useSaveToggle();
  const { data: m, isLoading, isError, error, refetch } = useQuery({ queryKey: ["member", id], queryFn: () => api(`/members/${id}`) });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/discover", { replace: true }));

  const respond = async (action, requestId) => {
    setBusy(action);
    try {
      await api(`/requests/${requestId}/${action}`, { method: "POST" });
      toast(action === "accept" ? `You are now connected with ${m?.display_name}` : action === "decline" ? "Request declined" : "Request withdrawn", action === "accept" ? "success" : "info");
      ["member", "requests", "unread", "connections", "matches", "notifications"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };

  if (isLoading) return <Loading />;
  if (isError || !m) return <div className="py-8"><ErrorState testId="member-error" message={error?.message ?? "Not found"} onRetry={() => refetch()} /><Button testId="member-error-back" title="Go back" variant="ghost" className="mx-auto flex" onClick={goBack} /></div>;
  const mission = m.mission ? MISSION_INFO[m.mission] : null;
  const isMe = m.id === user?.id;
  const details = [[Briefcase, "Occupation", m.occupation], [GraduationCap, "Education", m.education], [Heart, "Marital status", m.marital_status],
    [MessagesSquare, "Language", m.preferred_language], [Flag, "Nationality", m.nationality], [Plane, "Willing to relocate", m.willing_to_relocate]].filter(([, , v]) => !!v);
  const roundBtn = "flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow transition-transform hover:scale-105";

  return (
    <div data-testid="member-detail-screen" className="grid gap-6 pb-24 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:gap-10 md:pb-0">
      <div className="md:sticky md:top-24 md:self-start">
        <div className="relative -mx-4 aspect-[3/4] overflow-hidden bg-[#F4EBE6] md:mx-0 md:rounded-[20px]">
          <Gallery photos={m.photos} page={page} setPage={setPage} name={m.display_name} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent to-[rgba(42,39,39,0.82)] p-5 pt-16">
            <div className="mb-2 flex flex-wrap gap-1.5">
              {m.is_test_profile ? <Pill label="Test profile" testId="member-test-badge" /> : null}
              {m.verification_status === "verified" ? <Pill label="Verified Member" tone="gold" icon={CheckCircle2} testId="member-verification-label" /> : <Pill label="Unverified" icon={Shield} testId="member-verification-label" />}
              {m.online ? <Pill label="Active now" tone="success" testId="member-online" /> : null}
            </div>
            <h1 className="text-[36px] leading-tight text-[#FDFBF7]" data-testid="member-name">{m.display_name}, {m.age}</h1>
            <p className="text-sm text-[#FDFBF7]/90">{m.city}, {m.country}</p>
          </div>
          <button data-testid="member-back-button" aria-label="Go back" onClick={goBack} className={`${roundBtn} absolute left-3 top-10`}><ChevronLeft className="h-6 w-6" /></button>
          {!isMe ? (
            <div className="absolute right-3 top-10 flex gap-2">
              <button data-testid="member-save-button" aria-label={m.saved ? "Remove from saved" : "Save profile"} onClick={() => toggleSave(m.id, m.saved)} className={`${roundBtn} text-primary`}><Heart className={`h-[22px] w-[22px] ${m.saved ? "fill-primary" : ""}`} /></button>
              <button data-testid="member-more-button" aria-label="Report or block" onClick={() => setActions(true)} className={roundBtn}><MoreHorizontal className="h-[22px] w-[22px]" /></button>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div className="mb-6 flex items-center justify-between">
          {mission ? <Pill label={mission.label} tone="rose" icon={mission.Icon} testId="member-mission" className="px-3 py-1.5 text-xs" /> : <span />}
          {!isMe ? <div className="text-right" data-testid="member-compatibility"><span className="font-display text-[34px] font-bold text-primary">{m.compatibility}%</span><span className="ml-1 text-xs text-muted-foreground">compatible</span></div> : null}
        </div>
        {m.bio ? <><h2 className="mb-1 text-[24px]">About</h2><p className="mb-6 text-[15px] leading-6">{m.bio}</p></> : null}
        {m.relationship_expectations ? <><h2 className="mb-1 text-[24px]">Looking for</h2><p className="mb-6 text-[15px] leading-6">{m.relationship_expectations}</p></> : null}
        <h2 className="mb-3 text-[24px]">Details</h2>
        <div className="mb-6 grid grid-cols-2 gap-3">
          {details.map(([Icon, label, v]) => (
            <div key={label} className="flex items-start gap-2 rounded-xl border border-border bg-white p-3">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium">{v}</p></div>
            </div>
          ))}
        </div>
        {m.interests.length ? (
          <>
            <h2 className="mb-3 text-[24px]">Interests</h2>
            <div className="mb-6 flex flex-wrap gap-2">
              {m.interests.map((i) => {
                const shared = m.shared_interests.includes(i);
                return <span key={i} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm ${shared ? "bg-primary text-white" : "bg-[#F4EBE6]"}`}>{shared ? <Heart className="h-3 w-3 fill-white" /> : null}{i}</span>;
              })}
            </div>
          </>
        ) : null}
        <div className="mb-6 flex gap-3 rounded-xl bg-[#F6E3E4] p-4" data-testid="member-whatsapp-note">
          <Lock className="h-[18px] w-[18px] shrink-0 text-primary" />
          <p className="text-sm">{m.relation.connection_id ? "You are connected. Open Connections to chat on WhatsApp." : m.contact_note}</p>
        </div>
        {!isMe ? (
          <div className="fixed inset-x-0 bottom-16 z-30 border-t border-border bg-background/95 p-3 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0">
            {m.relation.connection_id ? (
              <Button testId="member-open-connections" title="Open Connections" icon={Users} className="w-full" onClick={() => navigate("/connections")} />
            ) : m.relation.received_request_id ? (
              <div className="flex gap-3">
                <Button testId="member-decline-button" title="Decline" variant="outline" loading={busy === "decline"} className="flex-1" onClick={() => respond("decline", m.relation.received_request_id)} />
                <Button testId="member-accept-button" title="Accept" loading={busy === "accept"} className="flex-1" onClick={() => respond("accept", m.relation.received_request_id)} />
              </div>
            ) : m.relation.sent_request_id ? (
              <Button testId="member-withdraw-button" title="Request sent · Withdraw" variant="outline" loading={busy === "withdraw"} className="w-full" onClick={() => respond("withdraw", m.relation.sent_request_id)} />
            ) : (
              <Button testId="member-request-button" title={user?.request_label ?? "Send request"} icon={Send} className="w-full" onClick={() => setConfirm(true)} />
            )}
          </div>
        ) : null}
      </div>
      <ConfirmRequestDialog target={confirm ? m : null} onClose={() => setConfirm(false)} />
      <MemberActionsDialog member={actions ? m : null} photoId={m.photos[page]?.split("/").pop() ?? null} onClose={() => setActions(false)} onBlocked={goBack} />
    </div>
  );
}
